import {
  ArrowRight,
  Gamepad2,
  HeartHandshake,
  Mail,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SupportRequestForm } from "../components/SupportRequestForm";

const lastUpdated = "3 August 2026";

function InfoPage({
  eyebrow,
  title,
  summary,
  sections,
  children,
}: {
  eyebrow: string;
  title: string;
  summary: string;
  sections?: Array<{ id: string; label: string }>;
  children: ReactNode;
}) {
  return (
    <article className="info-page" id="top">
      <header className="info-hero">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{summary}</p>
        <small>Last updated: {lastUpdated}</small>
      </header>
      <div className="info-layout">
        <nav aria-label={`${title} page contents`}>
          <strong>{sections?.length ? "On this page" : "Questions?"}</strong>
          {sections?.map((section) => (
            <a href={`#${section.id}`} key={section.id}>
              {section.label}
            </a>
          ))}
          <a href="#top">Back to the top</a>
          <hr />
          <p>Questions or privacy requests?</p>
          <Link to="/contact">Contact Topzle</Link>
        </nav>
        <div className="info-copy">{children}</div>
      </div>
    </article>
  );
}

export function AboutPage() {
  return (
    <article className="about-page">
      <section className="about-hero">
        <div>
          <span className="eyebrow">ABOUT TOPZLE</span>
          <h1>Made for the moments between “hello” and “one more round.”</h1>
          <p>
            Topzle is a social game room where friends can play, chat, and stay connected without
            jumping between apps.
          </p>
          <div>
            <Link className="button button-primary" to="/app">
              Start a room <ArrowRight />
            </Link>
            <Link className="button button-ghost" to="/games">
              See the games
            </Link>
          </div>
        </div>
        <div className="about-burst" aria-hidden="true">
          <span>XO</span>
          <span>⚄</span>
          <span>♞</span>
          <span>✎</span>
        </div>
      </section>
      <section className="about-values">
        <article>
          <Users />
          <h2>People first</h2>
          <p>The room keeps everyone together while the game changes around them.</p>
        </article>
        <article>
          <Sparkles />
          <h2>Playful by design</h2>
          <p>Clear controls, bright moments, and games that feel good on any screen.</p>
        </article>
        <article>
          <ShieldCheck />
          <h2>Choice and control</h2>
          <p>You decide when to use your camera, microphone, chat, and account.</p>
        </article>
      </section>
      <section className="azonova-band">
        <span className="eyebrow">WHO MAKES TOPZLE?</span>
        <h2>Topzle is a product by Azonova.</h2>
        <p>Azonova builds and operates Topzle.</p>
        <a className="text-link" href="https://azonova.com" target="_blank" rel="noreferrer">
          Visit azonova.com <ArrowRight />
        </a>
      </section>
    </article>
  );
}

export function ContactPage() {
  return (
    <article className="contact-page">
      <section className="contact-hero">
        <span className="eyebrow">CONTACT</span>
        <h1>Let’s talk.</h1>
        <p>
          Need help, want to report a safety concern, or have an idea for Topzle? Send us an email.
        </p>
        <a className="contact-email" href="mailto:contact@topzle.com">
          <Mail />
          <span>
            <small>EMAIL TOPZLE</small>
            <strong>contact@topzle.com</strong>
          </span>
          <ArrowRight />
        </a>
      </section>
      <section className="contact-grid">
        <article>
          <Gamepad2 />
          <h2>Game or account help</h2>
          <p>
            Tell us what happened, which game you were playing, and the room code if you have it.
          </p>
        </article>
        <article>
          <ShieldCheck />
          <h2>Safety and privacy</h2>
          <p>
            Put “Safety” or “Privacy” in the subject. Do not email passwords, payment card numbers,
            or private room media.
          </p>
        </article>
        <article>
          <HeartHandshake />
          <h2>Business and press</h2>
          <p>
            For partnerships, advertising, or media questions, tell us who you are and how we can
            help.
          </p>
        </article>
      </section>
      <section className="contact-form-section">
        <div>
          <span className="eyebrow">SEND A MESSAGE</span>
          <h2>We’ll track it from here.</h2>
          <p>
            Replies are sent from contact@topzle.com. Never include a password, sign-in code,
            payment card number, or private call recording.
          </p>
        </div>
        <SupportRequestForm context={{ source: "contact_page" }} />
      </section>
    </article>
  );
}

export function PrivacyPage() {
  return (
    <InfoPage
      eyebrow="PRIVACY"
      title="Your privacy on Topzle"
      summary="See what information Topzle uses, why it is needed, and the choices available to you."
      sections={[
        { id: "scope", label: "Scope and responsibility" },
        { id: "collection", label: "Information we collect" },
        { id: "use", label: "How and why we use it" },
        { id: "media", label: "Calls and on-device recognition" },
        { id: "sharing", label: "Sharing and visibility" },
        { id: "retention", label: "Retention and security" },
        { id: "rights", label: "Your choices and rights" },
        { id: "young-players", label: "Young players" },
        { id: "international", label: "International use" },
        { id: "updates", label: "Changes and contact" },
      ]}
    >
      <section id="scope">
        <h2>Scope and responsibility</h2>
        <p>
          This notice covers Topzle websites, game rooms, account and guest play, support, and
          future Topzle mobile apps. Topzle is a product operated by Azonova, which is responsible
          for the information described here. Third-party sites linked from Topzle apply their own
          notices.
        </p>
        <p>
          For privacy questions or requests, use our <Link to="/contact">contact form</Link> or
          email <a href="mailto:contact@topzle.com">contact@topzle.com</a>. We may need to verify
          that a requester controls the relevant account before disclosing, changing, or deleting
          information.
        </p>
      </section>
      <section id="collection">
        <h2>Information we collect</h2>
        <ul>
          <li>
            <strong>Account and profile:</strong> verified phone number, email, date of birth,
            age-safety group, guardian review status, display name, avatar, username, country flag,
            friends, blocked users, and preferences. Guests receive a temporary identity and may
            supply a display name and avatar.
          </li>
          <li>
            <strong>Rooms and communications:</strong> room identifiers, invitations, membership,
            game choices, moves, scores and results, chat messages, reactions, typing and message
            delivery/read state, reports, blocks, moderation actions, and related timestamps.
          </li>
          <li>
            <strong>Calls:</strong> when you turn a call control on, device permission state, call
            participants, start/end times, connection and quality diagnostics, signalling data, and
            live audio or video relayed to the room. Joining a room alone does not request camera or
            microphone permission.
          </li>
          <li>
            <strong>Device, network, and locale:</strong> IP address, browser/app and operating
            system, language, time zone, screen and viewport size, touch capability, connection
            type, identifiers, diagnostic logs, and fraud/security signals. We derive a coarse
            country from Cloudflare request metadata and use the device language and time zone for
            defaults. You can override the country in Settings. We do not send your IP address to
            external geolocation providers and do not infer precise GPS location.
          </li>
          <li>
            <strong>Support and safety:</strong> the contact details and message you submit, ticket
            history, acknowledgements and replies, relevant room/game identifiers, approximate
            locale, device context, attachments if supported, and evidence needed to investigate
            abuse or an account problem.
          </li>
          <li>
            <strong>Payments, analytics, and ads:</strong> plan, purchase status, receipt and
            billing identifiers when paid features launch, plus cookie, device, advertising, and
            usage information when those tools are enabled. Payment providers
            handle full card or bank details.
          </li>
        </ul>
        <p>
          Information comes from you, your device and activity, other players who interact with or
          report you, authentication and payment providers, security services, and public or lawful
          sources used to investigate misuse.
        </p>
      </section>
      <section id="use">
        <h2>How and why we use information</h2>
        <p>
          We use information to authenticate people, create guest sessions, provide matchmaking,
          rooms, games and social features, synchronise results, deliver communications, remember
          settings, offer local defaults, provide support, prevent fraud and abuse, enforce our
          rules, improve accessibility and reliability, measure performance, and meet legal duties.
        </p>
        <p>
          Depending on the activity and your location, the legal basis may be performing the service
          you request, your consent, Azonova’s legitimate interests in operating and protecting
          Topzle, compliance with law, or protection of someone’s vital interests. Withdrawing
          consent does not undo earlier lawful processing and may prevent an optional feature from
          working.
        </p>
        <p>
          Bots, matchmaking, spam controls, and moderation signals may make or recommend automated
          choices. A person should review reports that could lead to a significant account action;
          contact support if you believe an automated result or enforcement decision is wrong.
        </p>
      </section>
      <section id="media">
        <h2>Live calls and on-device recognition</h2>
        <p>
          Camera and microphone access begins only after you select the relevant control and your
          browser or operating system grants permission. The first time you start video, Topzle may
          also start audio so other players can hear you; you can then mute audio and stop video
          independently. Live media is relayed to current room participants through our call
          infrastructure and is not recorded by Topzle by default.
        </p>
        <p>
          Rock Paper Scissors hand recognition and Draw & Guess recognition run on your device when
          available. For camera recognition, Topzle sends the selected throw and confidence to the
          room—not the recognition frame or hand landmarks. Other players can still capture what
          they see or hear using their device, so keep private information out of view.
        </p>
      </section>
      <section id="sharing">
        <h2>Visibility, service providers, and disclosure</h2>
        <p>
          Your display name, avatar, optional country flag, presence, game actions and results are
          visible to relevant room participants. Public-room information may be shown to people
          looking for a match. Chat and live media are shared with the people in that room. Avoid
          putting real-world contact details in public-facing profile fields.
        </p>
        <p>
          Providers may process information for hosting, databases, calls, security, phone and email
          delivery, locale defaults, analytics, advertising, app distribution, support, and
          payments. Current or planned providers include Cloudflare, Supabase, Amazon Web Services,
          Google, and the provider shown at checkout. They may act for Azonova or under their own
          published terms, depending on the service.
        </p>
        <p>
          We may disclose information with your direction, to investigate abuse, protect a person,
          establish or defend legal claims, comply with valid legal process, or as part of a merger,
          financing, reorganisation, or sale with appropriate safeguards. We do not sell private
          room media. If advertising sharing that is legally treated as a “sale” or “share” is
          introduced, required opt-out controls will be provided before it begins.
        </p>
      </section>
      <section id="retention">
        <h2>Retention and security</h2>
        <p>
          Account and profile information is generally retained while an account remains open. Guest
          sessions, presence and typing signals can expire sooner. Game history, room events, chat,
          support, safety, payment, fraud-prevention, backup, and legal records may remain for
          different periods based on feature operation, user expectations, dispute and abuse
          prevention, and applicable law. Deleting an account does not instantly remove copies in
          backups, another player’s records, or material preserved for safety or legal reasons.
        </p>
        <p>
          We use access restrictions, encrypted transport, secret management, rate limits,
          monitoring, and provider security controls. No internet service can promise absolute
          security. Use unique sign-in methods, protect one-time codes, and report suspected account
          access promptly.
        </p>
      </section>
      <section id="rights">
        <h2>Your choices and rights</h2>
        <ul>
          <li>
            Use room controls to stop video, mute audio, hide avatars, leave, block, or report.
          </li>
          <li>Change camera and microphone permission in your browser or device settings.</li>
          <li>Use browser privacy controls to limit cookies and analytics requests.</li>
          <li>Update profile, country-flag visibility, communication, and privacy choices.</li>
          <li>Ask to access, correct, export, or delete information linked to you.</li>
          <li>
            Object to or restrict eligible processing, withdraw consent, opt out of eligible
            advertising sharing, or appeal an automated or moderation decision.
          </li>
          <li>Complain to the privacy or consumer authority available where you live.</li>
        </ul>
        <p>
          Rights and exceptions vary by location. We may retain information when a legal exception
          applies, when needed to protect others, or when we cannot reliably connect a request to
          the correct person. Authorised agents and guardians may be asked for proof of authority.
        </p>
      </section>
      <section id="young-players">
        <h2>Young players and guardians</h2>
        <p>
          Topzle accounts are not available to children under 13, and a higher minimum applies where
          local law requires it. Players aged 13–17 must provide a guardian contact for review. Date
          of birth is used to apply age-aware settings and is not displayed to other players. Teen
          free-text chat remains limited while guardian review is pending.
        </p>
        <p>
          We do not use a young player’s profile for behavioural advertising. A guardian may ask to
          review, correct, delete, or withdraw consent for eligible information. If you believe an
          under-age child or a teen without valid guardian involvement supplied information, contact
          us immediately; we may restrict the profile while reviewing it.
        </p>
      </section>
      <section id="international">
        <h2>International transfers and local law</h2>
        <p>
          Azonova and its providers may process information in countries other than yours. Where
          required, we use contractual, adequacy, consent, or other lawful transfer mechanisms. The
          exact rights, response periods, and lawful exceptions depend on where you live.
        </p>
      </section>
      <section id="updates">
        <h2>Changes and contact</h2>
        <p>
          We may update this notice when products, providers, or law change. We will update the date
          above and provide a prominent notice or request fresh consent when required for a material
          change. Contact <a href="mailto:contact@topzle.com">contact@topzle.com</a> or use the{" "}
          <Link to="/contact">support form</Link> for a privacy request.
        </p>
      </section>
    </InfoPage>
  );
}

export function TermsPage() {
  return (
    <InfoPage
      eyebrow="TERMS"
      title="Rules for using Topzle"
      summary="These terms are the agreement between you and Azonova when you use Topzle."
      sections={[
        { id: "agreement", label: "Agreement and eligibility" },
        { id: "accounts", label: "Accounts, guests, and rooms" },
        { id: "conduct", label: "Community and fair play" },
        { id: "content", label: "Your content and calls" },
        { id: "software", label: "Topzle software and bots" },
        { id: "moderation", label: "Moderation and appeals" },
        { id: "payments", label: "Payments and third parties" },
        { id: "availability", label: "Availability and termination" },
        { id: "responsibility", label: "Responsibility and disputes" },
        { id: "changes", label: "Changes and contact" },
      ]}
    >
      <section id="agreement">
        <h2>Agreement, eligibility, and guardians</h2>
        <p>
          By accessing Topzle as an account holder, guest, room host, player, or spectator, you
          agree to these Terms, the <Link to="/privacy">Privacy Notice</Link>, and the rules shown
          for a feature or purchase. If you do not agree, do not use Topzle. If you act for an
          organisation, you confirm you may bind it.
        </p>
        <p>
          You must be at least 13, or the higher minimum age required where you live. If you are
          under the age of legal adulthood, a parent or legal guardian must review your use and any
          consent request. Players aged 13–17 must complete Topzle’s guardian-review flow. You may
          not avoid age controls by using guest play or false information.
        </p>
        <p>
          You may use Topzle only where it is lawful and must comply with export, sanctions,
          consumer, communications, recording, and online-safety laws that apply to you.
        </p>
      </section>
      <section id="accounts">
        <h2>Accounts, guests, and rooms</h2>
        <ul>
          <li>Provide accurate, current details and never share one-time codes or credentials.</li>
          <li>Use one identity per person and do not sell, transfer, or impersonate an account.</li>
          <li>You are responsible for activity performed through your account or guest session.</li>
          <li>Public rooms may be discoverable; private codes and links are invitations.</li>
          <li>
            Do not join, remain in, or regain access to a room after being removed or blocked.
          </li>
          <li>Hosts manage room settings but do not speak for Azonova or override these Terms.</li>
          <li>Tell support promptly if you suspect unauthorised access or a compromised invite.</li>
        </ul>
        <p>
          Usernames, room names, avatars, and country flags must not mislead people, expose private
          information, violate rights, or contain prohibited content. Azonova may change or reclaim
          identifiers when reasonably necessary.
        </p>
      </section>
      <section id="conduct">
        <h2>Playing fairly and treating people well</h2>
        <p>You may not use Topzle to:</p>
        <ul>
          <li>harass, stalk, threaten, shame, blackmail, exploit, impersonate, or dox anyone;</li>
          <li>promote hate, violent extremism, self-harm, credible violence, or dangerous acts;</li>
          <li>
            share unlawful sexual content, non-consensual intimate content, or sexual solicitation;
          </li>
          <li>
            sexualise, groom, exploit, or endanger a child, or seek a child’s private images,
            location, credentials, or off-platform contact;
          </li>
          <li>
            sell or facilitate illegal or dangerous goods, fraud, gambling, or unlawful services;
          </li>
          <li>
            share another person’s private information, recording, or likeness without authority;
          </li>
          <li>cheat, collude, manipulate ratings, exploit bugs, or automate unfair play;</li>
          <li>
            spam, scrape, reverse engineer, probe, overload, introduce malware, or bypass access,
            moderation, rate, privacy, age, or security controls;
          </li>
          <li>infringe copyright, trademark, privacy, publicity, or other rights.</li>
        </ul>
        <p>
          Context matters, but attempts, coded conduct, encouragement, coordination, and harmful
          off-platform conduct connected to Topzle may also be considered. Do not retaliate against
          reporters or misuse reports to target someone.
        </p>
      </section>
      <section id="content">
        <h2>Your content, camera, microphone, chat, and drawings</h2>
        <p>
          You retain rights you already have in content you submit. You give Azonova a worldwide,
          non-exclusive, royalty-free licence to host, reproduce, transmit, display, adapt for
          technical formats, moderate, and otherwise process it only to operate, secure, support,
          and improve Topzle. The licence lasts while reasonably needed for those purposes and may
          continue for backups, safety evidence, legal duties, or copies other people legitimately
          retain.
        </p>
        <p>
          You promise that you have the necessary rights and permissions. Calls are not recorded by
          Topzle by default. Do not capture, train on, publish, or reuse another person’s voice,
          video, chat, drawing, or personal details without clear permission and any consent the law
          requires. A room invitation is not consent to record.
        </p>
      </section>
      <section id="software">
        <h2>Topzle software, game rules, bots, and AI</h2>
        <p>
          Azonova and its licensors own Topzle’s name, design, software, documentation, and original
          game art. Subject to these Terms, you receive a limited, personal, revocable,
          non-transferable licence to use the service. No source code, model, brand, or other right
          is granted except where law does not allow that restriction.
        </p>
        <p>
          Game rules shown in Topzle control the supported digital implementation and may differ
          from a regional house rule. Bots and on-device recognition can make mistakes. AI labels,
          hints, classifications, and bot moves are entertainment features—not professional advice
          or a promise of fairness, accuracy, or a particular difficulty.
        </p>
      </section>
      <section id="moderation">
        <h2>Moderation, enforcement, reports, and appeals</h2>
        <p>
          Azonova may review reports and relevant account, room, chat, game, device, or support
          records. Depending on severity, history, age, and risk, we may limit a feature, remove
          content, end a room, warn, mute, suspend, restrict, or permanently terminate an identity.
          We may preserve evidence or notify a provider, guardian, emergency service, or authority
          where reasonably necessary or legally required.
        </p>
        <p>
          We may act without advance notice when delay could cause harm, enable evasion, or impair
          an investigation. Where an appeal is appropriate, contact support with the decision,
          account details, and why it should be reconsidered. Repeated, fraudulent, or abusive
          appeals may be closed.
        </p>
      </section>
      <section id="payments">
        <h2>Paid plans, subscriptions, and refunds</h2>
        <p>
          When paid features are offered, the price, billing period, taxes, renewal terms, and
          included features will be shown before purchase. Subscriptions may renew until cancelled.
          You can cancel through the place you bought them or the account billing area; access may
          continue until the paid period ends.
        </p>
        <p>
          Refunds follow the offer shown at checkout, the payment or app-store rules, and rights
          that cannot be excluded by law. See our <Link to="/billing">Billing & Refunds page</Link>.
        </p>
        <h2>Ads and other services</h2>
        <p>
          Topzle may show ads or link to other services. Their content, products, and privacy
          practices are their responsibility. App stores, authentication, call, payment, and other
          providers may apply additional terms. Sponsored content will be identified where required;
          an advertisement is not an endorsement.
        </p>
      </section>
      <section id="availability">
        <h2>Service changes and availability</h2>
        <p>
          Games and features may change, pause, or end. We work to keep Topzle available and results
          accurate, but internet, browser, device, provider, and call services can be interrupted.
          We may impose reasonable usage, storage, room, call, or safety limits and may test or
          withdraw features.
        </p>
        <h2>Suspension, termination, and account deletion</h2>
        <p>
          You may stop using Topzle and request eligible account deletion. Azonova may suspend or
          terminate access for a breach, safety risk, legal requirement, prolonged inactivity,
          discontinued service, or conduct that threatens Topzle or others. Provisions that by
          nature should survive—including ownership, content permissions already exercised, payment
          obligations, disclaimers, and dispute terms—continue after termination.
        </p>
      </section>
      <section id="responsibility">
        <h2>Responsibility</h2>
        <p>
          Topzle is provided “as available” with reasonable care, but without a promise that every
          feature will be uninterrupted, secure, compatible, error-free, or preserve every message
          or result. To the fullest extent law allows, Azonova is not responsible for indirect,
          incidental, special, consequential, or punitive loss, lost data or opportunities, or
          another user’s conduct or content. Nothing excludes consumer guarantees, fraud,
          intentional misconduct, personal-injury responsibility, or another right that cannot
          lawfully be excluded or limited.
        </p>
        <h2>Disputes and changes</h2>
        <p>
          Contact us first with your account contact, the issue, and the resolution you seek so we
          can try to resolve a dispute informally. The mandatory consumer protections and courts
          available under applicable law remain available. These Terms do not require arbitration or
          waive a class remedy unless a separate, valid agreement presented to you expressly says
          so.
        </p>
      </section>
      <section id="changes">
        <h2>Changes, severability, and contact</h2>
        <p>
          We may update these Terms as Topzle or law changes. For a material change, we will provide
          reasonable notice and request agreement where required. If one provision cannot be
          enforced, the remaining provisions continue. A delay in enforcing a provision is not a
          waiver. Questions, copyright notices, and appeals can be sent through the{" "}
          <Link to="/contact">contact form</Link> or to{" "}
          <a href="mailto:contact@topzle.com">contact@topzle.com</a>.
        </p>
      </section>
    </InfoPage>
  );
}

export function CookiesPage() {
  return (
    <InfoPage
      eyebrow="COOKIES & ADS"
      title="Cookies, analytics, and advertising"
      summary="See what Topzle stores in your browser or app, why it is used, and which browser controls apply."
    >
      <section>
        <h2>What these tools are</h2>
        <p>
          Cookies are small files saved by a website. Mobile apps use similar device identifiers.
          They can remember choices, keep you signed in, measure what works, and help choose or
          count ads.
        </p>
      </section>
      <section>
        <h2>Essential tools</h2>
        <p>
          These keep rooms, sign-in, security, and saved choices working. They are needed to provide
          the service you ask for and cannot always be switched off inside Topzle.
        </p>
      </section>
      <section>
        <h2>Preferences</h2>
        <p>
          These remember choices such as sound, layout, language, or whether chat starts closed.
          Clearing browser or app storage may reset them.
        </p>
      </section>
      <section>
        <h2>Analytics</h2>
        <p>
          Google Analytics measures which pages and games are popular and where errors happen.
          Analytics is enabled by default and Topzle does not provide an in-product switch to turn
          it off. We do not send names, email addresses, room chat, room codes, drawings, or live
          media to Google Analytics.
        </p>
      </section>
      <section>
        <h2>Advertising</h2>
        <p>
          If enabled, Google AdSense, Google AdMob, or another ad partner may use cookies, IP
          addresses, device or advertising identifiers, and activity to deliver, limit, and measure
          ads. Ads may be based on your interests only when allowed by your choices and local law.
          Child-directed areas will not use personalised advertising.
        </p>
      </section>
      <section>
        <h2>Your controls</h2>
        <ul>
          <li>Use browser cookie controls or tracking protection to limit analytics storage.</li>
          <li>Change browser cookie settings or reset your mobile advertising identifier.</li>
          <li>
            Use{" "}
            <a href="https://adssettings.google.com" target="_blank" rel="noreferrer">
              Google’s ad settings
            </a>{" "}
            to control personalised Google ads.
          </li>
          <li>Contact us with privacy or analytics questions.</li>
        </ul>
        <p>
          Blocking all storage may stop sign-in, rooms, or saved preferences from working. Questions
          can be sent to <a href="mailto:contact@topzle.com">contact@topzle.com</a>.
        </p>
      </section>
    </InfoPage>
  );
}

export function BillingPage() {
  return (
    <InfoPage
      eyebrow="BILLING & REFUNDS"
      title="Billing and refunds"
      summary="Prices, renewals, cancellations, and refunds for future paid plans and purchases."
    >
      <section>
        <h2>Before you pay</h2>
        <p>
          Topzle will show the total price, currency, taxes, billing period, included features, and
          whether a purchase renews before you confirm it. Paid features only apply after they
          launch.
        </p>
      </section>
      <section>
        <h2>Subscriptions</h2>
        <p>
          A subscription may renew at the shown interval until you cancel. Cancel from your Topzle
          billing area or from the app store or payment service where you bought it. Unless the
          offer says otherwise, you can use paid features until the current billing period ends.
        </p>
      </section>
      <section>
        <h2>Trials and price changes</h2>
        <p>
          Trial length and what happens after it ends will be shown before you begin. We will give
          advance notice of a subscription price change where required, and you can cancel before it
          applies.
        </p>
      </section>
      <section>
        <h2>Refunds</h2>
        <p>
          Refund eligibility follows the offer at checkout, the rules of the payment provider or app
          store, and your legal consumer rights. Duplicate or unauthorised charges should be
          reported promptly. Include the receipt number, but never send a full card number.
        </p>
      </section>
      <section>
        <h2>Payment help</h2>
        <p>
          Email <a href="mailto:contact@topzle.com">contact@topzle.com</a> with the account email,
          purchase date, and receipt or order number.
        </p>
      </section>
    </InfoPage>
  );
}
